import { Component, EventEmitter, Input, Output } from "@angular/core"
import { CURRENT_STATE_PROJECT_KEY, Linqset, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE, getInsights } from "../../common/linqset-model";
import { LinqsetRepositoryService } from "../../model-services/linqset-repository.service";
import { NodeService } from "../../model-services/node.service";
import { GroupNodeTag } from "../../canvas/model/graph-model";
import { MODEL_COLOURS } from "../../model-services/model-item-enums";
import { NODE_TYPE_DATA_TYPE, NodeTypeData } from "./node-type-data";
import { AppComponent } from "../../app.component";
import { findTagForOrganisation, getUnsortedModelList, TagGroup } from "../../common/organisation";
import { IGraph } from "@yfiles/yfiles";
import { totalNodeInsights } from "../../common/model-processing";
import { InsightsService } from "../../model-services/insights.service";

@Component({
    selector: 'app-node-types',
    templateUrl: './node-types.html',
    styleUrls: ['node-types.scss'],
    standalone: false
})
export class NodeTypesComponent {
    @Input() linqset:Linqset
    @Input() comparison:Linqset
    @Input() graph:IGraph
    @Input() tags:string[]
    @Input() excludeNotes:boolean

    selectedIndex:number
    selectedChildIndex:number
    types: NodeTypeData[]

    NODE_SUPERTYPE=NODE_SUPERTYPE

    @Output() selectTypeEvent = new EventEmitter<NodeTypeData>();
    // true while a type's nodes are being fetched, so the page can show it's busy from the click rather than the reply
    @Output() loadingTypeEvent = new EventEmitter<boolean>();

    constructor(public linqsetRepo: LinqsetRepositoryService, private nodeService:NodeService, private insightsService:InsightsService) {
    }

    refreshData(linqset:Linqset, comparison:Linqset, clear:boolean, isGrouped:boolean, tagGroup:TagGroup) {
        this.linqset = linqset
        this.comparison = comparison
        if (clear) {
            this.selectedIndex = undefined
            this.selectedChildIndex = undefined
        }
        this.populateData(isGrouped, tagGroup)
    }

    private populateData(isGrouped:boolean, tagGroup:TagGroup) {
        if (this.linqset.isOrganisationModel()) {
            const modelType = this.createModelType()

            this.types = [
                this.createInputType(),
                this.createType(NODE_SUPERTYPE.PEOPLE),
                this.createType(NODE_SUPERTYPE.ROLE),
                this.createType(NODE_SUPERTYPE.INFRASTRUCTURE),
                this.createType(NODE_SUPERTYPE.SYSTEM),
                this.createLazyType(NODE_SUPERTYPE.ACTION),
                this.createLazyType(NODE_SUPERTYPE.INFORMATION),
                this.createType(NODE_SUPERTYPE.OUTCOMES),
                modelType
            ]
            if (!this.excludeNotes) this.types.push(this.createType(NODE_SUPERTYPE.NOTES))

        }else if (this.linqset.isFilterModel()) {
            if (this.linqset.hasModelNodes) {
                this.types = [
                    this.createInputType(),
                    this.createType(NODE_SUPERTYPE.ROLE),
                    this.createType(NODE_SUPERTYPE.SYSTEM),
                    this.createType(NODE_SUPERTYPE.ACTION),
                    this.createType(NODE_SUPERTYPE.INFORMATION),
                    this.createOutputType(),
                    this.createType(NODE_SUPERTYPE.OUTCOMES)
                ]
            }else {
                this.types = [
                    this.createInputType(),
                    this.createType(NODE_SUPERTYPE.ROLE),
                    this.createType(NODE_SUPERTYPE.SYSTEM),
                    this.createLazyType(NODE_SUPERTYPE.ACTION),
                    this.createLazyType(NODE_SUPERTYPE.INFORMATION),
                    this.createType(NODE_SUPERTYPE.OUTCOMES)
                ]
            }
            const people = this.createType(NODE_SUPERTYPE.PEOPLE)
            const infrastructure = this.createType(NODE_SUPERTYPE.INFRASTRUCTURE)
            if (people.length > 0) this.types.push(people)
            if (infrastructure.length > 0) this.types.push(infrastructure)

            if (this.linqset.modelKeys?.length > 1) {
                this.types.push(this.createModelType())
            }
        }else {
            this.types = [
                this.createInputType(),
                this.createType(NODE_SUPERTYPE.ROLE),
                this.createType(NODE_SUPERTYPE.SYSTEM),
                this.createType(NODE_SUPERTYPE.ACTION),
                this.createType(NODE_SUPERTYPE.INFORMATION),
                this.createOutputType(),
                this.createType(NODE_SUPERTYPE.OUTCOMES)
            ]
            if (!this.excludeNotes) this.types.push(this.createType(NODE_SUPERTYPE.NOTES))
        }
        this.addGroupData(isGrouped, tagGroup)

        if (this.selectedIndex >= 0 && this.selectedChildIndex >= 0) this.selectChildType(this.selectedChildIndex)
        else this.selectType(this.selectedIndex)
    }

    isHelpOn() {
        return AppComponent.help
    }

    getHelpMessage() {
        return this.types[this.selectedIndex].helpMessage
    }

    addGroupData(isGrouped:boolean, tagGroup:TagGroup) {
        this.types = this.types.filter(t => t.dataType !== NODE_TYPE_DATA_TYPE.GROUP)
        let groupNodeData:GroupNodeTag[] = null
        let name = ""

        if (isGrouped && this.graph) {
            groupNodeData = this.graph.nodes.filter(n => n.tag.group).map(n => n.tag.group).toArray()
            name = "Groups"
        }else if (tagGroup && !this.linqset.isOrganisationModel()) {
            groupNodeData = this.generateTagGroupData(tagGroup)
            name = tagGroup.name
        }else if (this.tags && this.tags.length > 0) {
            groupNodeData = this.generateTagGroupDataUsingTags()
            name = "Tags"
        }

        if (groupNodeData && groupNodeData.length > 0) {
            const td = new NodeTypeData(name, NODE_TYPE_DATA_TYPE.GROUP, MODEL_COLOURS.ACTION_COLOR, MODEL_COLOURS.ACTION_HOVER_COLOR)
            td.groups = groupNodeData
            this.types.push(td)
        }
    }

    generateTagGroupDataUsingTags():GroupNodeTag[] {
        return this.tags.map(t => {
            const tag = findTagForOrganisation(this.linqset.organisation, t)
            const nodes = this.linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION && n.properties?.tagKeys && n.properties?.tagKeys[tag.group._key] === t)
            const group = new GroupNodeTag(this.linqset, t, tag.tag.name, tag.tag.description, undefined, tag.tag)
            group.insights = totalNodeInsights(this.linqset, nodes, false)
            return group
        })        
    }

    generateTagGroupData(tagGroup:TagGroup):GroupNodeTag[] {
        const map = this.linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION && !n.excluded).reduce((m,n) => {
            const tagKey = (n.properties?.tagKeys && n.properties.tagKeys[tagGroup._key]) || "notagkey"
            if (!m[tagKey]) m[tagKey] = []
            m[tagKey].push(n)
            return m
        }, {})

        return Object.keys(map).map(tagKey => {
            let group:GroupNodeTag
            if (tagKey === "notagkey")
                group = new GroupNodeTag(this.linqset, "notagkey", "No Tag", "Nodes without tag")
            else {
                const tag = tagGroup.tags.find(t => t._key === tagKey)
                group = new GroupNodeTag(this.linqset, tagKey, tag.name, tag.description, undefined, tag)
            }
            group.insights = totalNodeInsights(this.linqset, map[tagKey], false)
            return group
        })        
    }

    createModelType() {
        const td = new NodeTypeData("Models", NODE_TYPE_DATA_TYPE.MODELS, MODEL_COLOURS.MODEL_NODE_COLOR, MODEL_COLOURS.MODEL_NODE_HOVER_COLOR, undefined, NODE_TYPE.MODEL)
        if (this.linqset.modelKeys && this.linqset.modelKeys.length > 0) {
            td.models = this.linqset.organisation.models.filter(m => !m.deletedDate && this.linqset.modelKeys.indexOf(m._key) >= 0)
            // the same models as the current state has them: those made in the project aren't there
            td.comparisonModels = this.comparison && getUnsortedModelList(this.linqset.organisation).filter(m => this.linqset.modelKeys.indexOf(m._key) >= 0)
        }else {
            td.models = getUnsortedModelList(this.linqset.organisation, this.linqset.project)
            td.comparisonModels = this.comparison && getUnsortedModelList(this.linqset.organisation, this.comparison.project)
        }
        return td
    }

    async selectType(index:number) {
        const type = index >= 0 ? this.types[index] : undefined
        // the tile shows as chosen straight away, and the page as busy while a lazily loaded type's nodes are fetched
        this.selectedIndex = index
        this.selectedChildIndex = undefined
        this.loadingTypeEvent.emit(!!type?.lazy)
        if (type?.lazy) {
            try {
                const nodes = await this.insightsService.loadNodesBySuperType(this.linqset.organisation, this.linqset.workspaceKey, type.superType, this.linqset.filter)
                if (nodes) {
                    type.list = nodes
                    type.comparisonList = this.comparison?await this.insightsService.loadNodesBySuperType(this.linqset.organisation, CURRENT_STATE_PROJECT_KEY, type.superType, this.comparison.filter):[]
                    type.lazy = false
                }
            } finally {
                // another type chosen while these loaded is left as it is; its own choice has already been sent
                if (this.selectedIndex === index) this.loadingTypeEvent.emit(false)
            }
            if (this.selectedIndex !== index) return
        }
        this.selectTypeEvent.emit(type)
    }

    selectChildType(index:number) {
        this.selectedChildIndex = index
        this.selectTypeEvent.emit(this.types[this.selectedIndex].subTypes[index])
    }

    getTypeData(nodeList, dataType:NODE_TYPE_DATA_TYPE) {
        return nodeList.reduce((a,n) => {
            let typeData = a.find(t => t.type === n.type)
            if (!typeData) {
                const typeInfo = this.nodeService.getNodeType(n.type)

                typeData = new NodeTypeData(typeInfo.name, dataType, typeInfo.superType.colour, typeInfo.superType.hoverColour, n.superType, n.type)
                typeData.list = []
                a.push(typeData)
            }
            typeData.list.push(n)
            return a
        }, [])
    }

    createLazyType(superType:NODE_SUPERTYPE) {
        const typeInfo = superType && this.nodeService.getSuperTypeInfo(superType)
        const d = new NodeTypeData(typeInfo.name, NODE_TYPE_DATA_TYPE.STANDARD, typeInfo.colour, typeInfo.hoverColour, superType, typeInfo?.nodeTypes[0].type)
        d.lazy = true
        d.count = this.linqset.insights?.superTypes[superType]?.number || 0
        d.comparisonCount = this.comparison?.insights?.superTypes[superType]?.number || 0
        return d
    }

    createType(superType:NODE_SUPERTYPE) {
        const typeInfo = this.nodeService.getNodeTypeForSuperType(superType)
        const list = this.linqset.nodes.filter(n => !n.excluded && n.superType === superType)
        const compareList = this.comparison && this.comparison.nodes.filter(n => !n.excluded && n.superType === superType)

        return this.createTypeData(typeInfo.name, list, superType==NODE_SUPERTYPE.OUTCOMES?NODE_TYPE_DATA_TYPE.OUTPUT:NODE_TYPE_DATA_TYPE.STANDARD, compareList, superType)
    }

    createInputType() {
        const list = this.linqset.nodes.filter(n => !n.excluded && getInsights(n, this.linqset).isInput)
        const compareList = this.comparison && this.comparison.nodes.filter(n => !n.excluded && getInsights(n, this.comparison).isInput)

        return this.createTypeData("Input", list, NODE_TYPE_DATA_TYPE.INPUT, compareList, NODE_SUPERTYPE.ACTION, NODE_TYPE.CAPTURE)
    }

    createOutputType() {
        const list = this.linqset.nodes.filter(n => !n.excluded && getInsights(n, this.linqset).isOutput)
        const compareList = this.comparison && this.comparison.nodes.filter(n => !n.excluded && getInsights(n, this.comparison).isOutput)

        return this.createTypeData("Output", list, NODE_TYPE_DATA_TYPE.OUTPUT, compareList, NODE_SUPERTYPE.INFORMATION)
    }

    createTypeData(name:string, nodeList:LinqsetNode[], dataType:NODE_TYPE_DATA_TYPE, comparisonList:LinqsetNode[], superType?:NODE_SUPERTYPE, type?:NODE_TYPE) {
        const typeInfo = superType && this.nodeService.getSuperTypeInfo(superType)

        const td = new NodeTypeData(name, dataType, typeInfo?typeInfo.colour:MODEL_COLOURS.ACTION_COLOR, typeInfo?typeInfo.hoverColour:MODEL_COLOURS.ACTION_HOVER_COLOR, superType, type || typeInfo?.nodeTypes[0].type)
        td.list = nodeList.sort((a,b) => a.name?.toLowerCase() > b.name?.toLowerCase()?1:0)
        td.subTypes = this.getTypeData(nodeList, dataType)
        td.comparisonList = comparisonList
        return td
    }
    
}
